import React from 'react';
import { CarIcon } from 'lucide-react';
import { useAppState } from '../contexts/AppStateContext';
import { useUi } from '../contexts/UiContext';
import { DEMO_DATE } from '../data/demo';
import { schoolKindLabels } from '../data/labels';
import { formatShortDay } from '../utils/time';
interface UpcomingRow {
  key: string;
  date: string;
  start?: string;
  title: string;
  sub?: string;
  car?: {
    text: string;
    ok: boolean;
  };
}

/** Posebni dogodki in šolske obveznosti, ki prihajajo. Stalni tedenski termini (npr. plavanje) so v delih dneva na Danes. */
export function NextUpCard() {
  const {
    family,
    viewer,
    memberById,
    db,
    isAdmin
  } = useAppState();
  const {
    setTab
  } = useUi();
  const withWhom = (ids: string[]) => {
    if (ids.length === 0) return 'vsa družina';
    const others = ids.filter(id => id !== viewer.id).map(id => memberById(id)?.name).filter(Boolean);
    if (!ids.includes(viewer.id)) return others.join(', ');
    return others.length === 0 ? '' : others.length > 2 ? 'skupaj z drugimi' : `z ${others.join(' in ')}`;
  };
  const events: UpcomingRow[] = family.events.filter(e => !e.repeat && e.date >= DEMO_DATE && (e.participantIds.length === 0 || e.participantIds.includes(viewer.id) || e.driverId === viewer.id)).map(e => ({
    key: e.id,
    date: e.date,
    start: e.start,
    title: e.title,
    sub: withWhom(e.participantIds),
    car: e.driverId ? {
      text: `Pelje ${memberById(e.driverId)?.name}`,
      ok: true
    } : e.needsDriver ? {
      text: 'Kdo pelje? Še nihče',
      ok: false
    } : undefined
  }));
  const school: UpcomingRow[] = db.schoolItems.filter(s => s.familyId === family.id && s.date >= DEMO_DATE && !s.done && (isAdmin || s.memberId === viewer.id)).map(s => ({
    key: s.id,
    date: s.date,
    title: s.title,
    sub: `${schoolKindLabels[s.kind]}${isAdmin && s.memberId !== viewer.id ? ` · ${memberById(s.memberId)?.name}` : ''}`
  }));
  const upcoming = [...events, ...school].sort((a, b) => (a.date + (a.start ?? '99')).localeCompare(b.date + (b.start ?? '99'))).slice(0, 4);
  return <section aria-labelledby="nextup-title" className="rounded-3xl border-[1.5px] border-line bg-surface p-4 shadow-card" data-id="element-280" data-mp="2bd78lz9oboh3">
      <div className="flex items-baseline justify-between" data-id="element-281" data-mp="2pcyjhr4w8oqv">
        <h2 id="nextup-title" className="font-display text-lg font-semibold" data-id="element-282" data-mp="2la24sbih5cs7">
          Kaj prihaja
        </h2>
        <button type="button" onClick={() => setTab('koledar')} className="text-xs font-bold text-info-ink hover:underline" data-id="element-283" data-mp="2z9kyhzajglkz">
          Koledar
        </button>
      </div>
      {upcoming.length === 0 ? <p className="mt-2 text-sm text-muted" data-id="element-284" data-mp="38z4ij89zmlhf">Nič posebnega na obzorju. Redka priložnost za dolgčas.</p> : <ul className="mt-2 space-y-3" data-id="element-285" data-mp="341l68dwuois4">
          {upcoming.map(r => <li key={r.key} className="flex gap-3" data-id="element-286" data-mp="3i02se773xn7x">
              <span className="w-14 shrink-0 pt-0.5 text-sm font-extrabold tabular-nums" data-id="element-287" data-mp="3hyhv49terq6d">
                {r.date === DEMO_DATE ? r.start ?? 'danes' : <span className="text-xs capitalize" data-id="element-288" data-mp="hnamcpoguj4">{formatShortDay(r.date)}</span>}
              </span>
              <span className="min-w-0 flex-1" data-id="element-289" data-mp="5hi66tkee8ai">
                <span className="block font-bold leading-snug" data-id="element-290" data-mp="5f5nufaq9vwx">{r.title}</span>
                {r.sub && <span className="block text-xs text-muted" data-id="element-291" data-mp="3nmlf6pdhj4g7">{r.sub}</span>}
                {r.car && <span className={`mt-0.5 inline-flex items-center gap-1 text-xs font-semibold ${r.car.ok ? 'text-info-ink' : 'text-review-ink'}`} data-id="element-292" data-mp="3rmn8aa55mpjv">
                    <CarIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-293" data-mp="3io1q4g3lhcjm" />
                    {r.car.text}
                  </span>}
              </span>
            </li>)}
        </ul>}
    </section>;
}